import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, InputAdornment,
  Chip, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Search } from "lucide-react";
import { useColorMode } from "../../../theme/ColorModeContext";

import axiosInstance from "../../../services/axiosInstance";
import { useTranslation } from "react-i18next";

const STATUSES = ["", "ANSWER", "NOANSWER", "BUSY", "FAILED"];

function fmtDuration(secs) {
  if (!secs && secs !== 0) return "—";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatusChip({ status }) {
  const { t } = useTranslation("admin");
  const map = {
    ANSWER:   { color: "#22C55E", tkey: "statusAnswered" },
    NOANSWER: { color: "#EF4444", tkey: "statusMissed"   },
    BUSY:     { color: "#F59E0B", tkey: "statusBusy"     },
    FAILED:   { color: "#6B7280", tkey: "statusFailed"   },
  };
  const cfg = map[status] || { color: "#6B7280", tkey: null };
  return (
    <Chip
      label={cfg.tkey ? t(cfg.tkey) : (status || t("statusUnknown"))}
      size="small"
      sx={{ height: 22, fontSize: "0.6875rem", fontWeight: 600, background: alpha(cfg.color, 0.1), color: cfg.color, border: "none" }}
    />
  );
}

function CallLogsAdmin() {
  const { t }    = useTranslation("admin");
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [rowsPerPage]         = useState(20);
  const [search,  setSearch]  = useState("");
  const [status,  setStatus]  = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo,   setDateTo]   = useState(today);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const fetchLogs = useCallback(() => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams({
      page: page + 1,
      limit: rowsPerPage,
      ...(search   ? { search }          : {}),
      ...(status   ? { status }          : {}),
      ...(dateFrom ? { dateFrom }        : {}),
      ...(dateTo   ? { dateTo }          : {}),
    });
    axiosInstance.get(`/admin/call-logs?${params}`)
      .then(r => { setRows(r.data.calls ?? []); setTotal(r.data.total || 0); })
      .catch(e => setError(e.response?.data?.message || t("failedLoadCallLogs")))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, search, status, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const glassSx = { background: "var(--surface-card)", border: "1px solid var(--line-200)", boxShadow: "var(--shadow-card)", borderRadius: "18px" };
  const cellSx  = { fontSize: "0.8125rem", borderColor: "var(--line-200)", py: 1.25 };
  const headSx  = { ...cellSx, fontWeight: 700, color: "text.secondary", background: isLight ? alpha("#000", 0.025) : alpha("#fff", 0.025) };

  return (
    <Box sx={{ maxWidth: 1300, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", color: "text.primary", mb: 0.5 }}>{t("callLogsTitle")}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("callLogsSub")}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          placeholder={t("searchCallerCalled")}
          size="small"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.875rem" } }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={{ fontSize: "0.875rem" }}>{t("statusLabel")}</InputLabel>
          <Select value={status} label={t("statusLabel")} onChange={e => { setStatus(e.target.value); setPage(0); }} sx={{ borderRadius: "10px", fontSize: "0.875rem" }}>
            {STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: "0.875rem" }}>{s || t("allStatuses")}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          label={t("fromDate")} type="date" size="small" value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.875rem" } }}
        />
        <TextField
          label={t("toDate")} type="date" size="small" value={dateTo}
          onChange={e => { setDateTo(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.875rem" } }}
        />
      </Box>

      {/* Summary bar */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem" }}>
          {loading ? t("loadingEllipsis") : t("totalCallsCount", { count: total.toLocaleString() })}
        </Typography>
      </Box>

      {/* Table */}
      <Box sx={{ ...glassSx, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>{t("colTime")}</TableCell>
                <TableCell sx={headSx}>{t("colCaller")}</TableCell>
                <TableCell sx={headSx}>{t("colAgent")}</TableCell>
                <TableCell sx={headSx}>{t("colSection")}</TableCell>
                <TableCell sx={headSx}>{t("colTenant")}</TableCell>
                <TableCell sx={headSx}>{t("colStatus")}</TableCell>
                <TableCell sx={headSx}>{t("colDuration")}</TableCell>
                <TableCell sx={headSx}>{t("colRing")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "text.secondary", fontSize: "0.875rem" }}>{t("noCallLogsFound")}</TableCell></TableRow>
              ) : rows.map((row, i) => (
                <TableRow key={row.id ?? i} hover sx={{ "&:last-child td": { border: 0 } }}>
                  <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: "text.secondary" }}>
                    {fmtDate(row.startTime)}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                    {row.callerNumber || "—"}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                    {row.answeredAgent || "—"}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, color: "text.secondary" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <span>{row.sectionName || "—"}</span>
                      {row.selectedLanguage && (
                        <Chip
                          label={row.selectedLanguage.slice(0, 2).toUpperCase()}
                          size="small"
                          sx={{ height: 14, fontSize: "0.5rem", fontWeight: 700, color: "#06B6D4", background: alpha("#06B6D4", 0.08), border: `1px solid ${alpha("#06B6D4", 0.2)}`, "& .MuiChip-label": { px: 0.5 } }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.8125rem", lineHeight: 1.2 }}>{row.tenantName || "—"}</Typography>
                      <Typography sx={{ fontSize: "0.6875rem", color: "text.secondary" }}>{row.enCompanyName || ""}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <StatusChip status={row.status} />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }}>
                    {fmtDuration(row.duration)}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                    {fmtDuration(row.ringDuration)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[20]}
          onPageChange={(_, p) => setPage(p)}
          sx={{ borderTop: "1px solid var(--line-200)", "& .MuiTablePagination-displayedRows": { fontSize: "0.8125rem" } }}
        />
      </Box>
    </Box>
  );
}

export default CallLogsAdmin;
